import {useCompanies, useDealers, useParentCompanies} from '@api'
import {
  currencies,
  FormFieldItem,
  generateDropdownOptions,
  getUserData,
  icons,
  isSuperAdmin,
  isCompanyUser,
  isCompanyAdmin,
  useViewNavigate,
} from '@common'
import {Country, FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {SvgIcon, Typography, useMediaQuery, useTheme} from '@mui/material'
import {
  handleNavigateAtom,
  getUserSelectionAtom,
  setUserSelectionAtom,
  UserSelection,
  setCompanyCurrencyAtom,
  userPreferencesHydratedAtom,
} from '@state'

import {useCallback, useEffect, useMemo, useState} from 'react'

import {useAtom, useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

import {CustomRenderValueWrapper, selectIconStyle} from './style'

export function useTopBarModel() {
  const {t} = useTranslation()
  const currentUser = getUserData()
  const navigate = useViewNavigate()

  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const [, setUserSelection] = useAtom(setUserSelectionAtom)

  const [, setCompanyCurrency] = useAtom(setCompanyCurrencyAtom)
  const hydrated = useAtomValue(userPreferencesHydratedAtom)

  const handleNavigate = useSetAtom(handleNavigateAtom)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)

  const theme = useTheme()
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'))

  const {data: dealersData} = useDealers()
  const {data: companiesData} = useCompanies()
  const {data: parentCompaniesData} = useParentCompanies(companiesData?.[0].parentCompanyId)

  const dropdownOptions = useMemo(() => {
    if (!companiesData || !dealersData || !parentCompaniesData || !userSelection)
      return {
        dealers: [],
        companies: [],
        parentCompanies: [],
      }
    else
      return {
        parentCompanies: generateDropdownOptions(parentCompaniesData),
        companies: generateDropdownOptions(
          companiesData
            ?.filter(company => company.parentCompanyId === userSelection.parentCompanyId)
            .filter(item => isSuperAdmin() || currentUser.companyIds.includes(item.id))
            .map(item => {
              return {
                ...item,
                icon: item.country,
              }
            }),
          'name',
          'id',
          'icon',
        ),
        dealers: [
          {label: t(TranslationKey['All Dealers']), value: 'all'},
          ...generateDropdownOptions(dealersData?.filter(dealer => dealer.companyId === userSelection.companyId)),
        ],
      }
  }, [companiesData, dealersData, parentCompaniesData, userSelection, t])

  const setCurrencyForCompany = (companyId?: string) => {
    if (!companyId || !companiesData) return
    const key = companiesData?.find(c => c.id === companyId)?.country as Country
    const currency = currencies[key]
    if (currency) setCompanyCurrency({companyId, currency})
  }

  const getFirstDealerIdForCompany = (companyId?: string) =>
    dealersData?.find(d => d.companyId === companyId)?.id || 'all'

  const getFirstCompanyIdForParent = (parentCompanyId?: string) =>
    companiesData?.find(c => c.parentCompanyId === parentCompanyId)?.id || ''

  useEffect(() => {
    if (!hydrated) return
    if (userSelection) return
    if (!companiesData?.length) return

    let firstCompany = companiesData[0]
    let dealerId = getFirstDealerIdForCompany(firstCompany.id)

    if (isCompanyAdmin()) {
      firstCompany = companiesData.filter(item => currentUser.companyIds.includes(item.id))[0]
    }
    if (isCompanyUser()) dealerId = currentUser.dealerId || 'all'
    const dealerCompany = companiesData.find(
      company => company.id === dealersData?.find(dealer => dealer.id === dealerId)?.companyId,
    )
    if (dealerCompany) firstCompany = dealerCompany

    setUserSelection({
      userId: currentUser.id,
      parentCompanyId: firstCompany.parentCompanyId,
      companyId: firstCompany.id,
      dealerId,
    })

    setCurrencyForCompany(firstCompany.id)
  }, [dealersData, companiesData, parentCompaniesData])

  /**
   * A function for custom DropdownItem render
   */
  const renderValue = useCallback(() => {
    const foundCompany = companiesData?.find(item => item.id === userSelection?.companyId)
    return (
      <CustomRenderValueWrapper>
        <Typography>{foundCompany?.name}</Typography>
        {foundCompany && <SvgIcon style={selectIconStyle}>{icons[foundCompany?.country]}</SvgIcon>}
      </CustomRenderValueWrapper>
    )
  }, [companiesData, userSelection])

  /**
   * A function to handle selection change
   * @param event Change event
   */
  const onChangeSelection = (newValue: UserSelection) => {
    handleNavigate(() => {
      setUserSelection(newValue)
      setCurrencyForCompany(newValue.companyId)

      navigate(NavigationPathKey.DASHBOARD)
    })
  }

  /**
   * Dropdown form fields
   */
  const formFields: FormFieldItem[] = useMemo(
    () => [
      {
        fieldType: FormFieldType.DROPDOWN,
        items: dropdownOptions.dealers,
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          const value = event.target.value
          if (userSelection) onChangeSelection({...userSelection, dealerId: value})
        },
        value: userSelection?.dealerId,
        placeholder: t(TranslationKey.Dealer),
        style: {height: '2rem', paddingRight: '0.5rem'},
        disabled: isCompanyUser(),
      },
      {
        fieldType: FormFieldType.DROPDOWN,
        items: dropdownOptions.companies,
        value: userSelection?.companyId,
        renderValue: renderValue,
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          const value = event.target.value
          const dealerId = getFirstDealerIdForCompany(value)
          if (userSelection) onChangeSelection({...userSelection, companyId: value, dealerId})
        },
        placeholder: t(TranslationKey.Company),
        style: {height: '2rem', paddingRight: '0.5rem'},
        disabled: isCompanyAdmin() || isCompanyUser(),
      },
      {
        fieldType: FormFieldType.DROPDOWN,
        items: dropdownOptions.parentCompanies,
        value: userSelection?.parentCompanyId,
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          const value = event.target.value
          const companyId = getFirstCompanyIdForParent(value)
          const dealerId = getFirstDealerIdForCompany(companyId)
          if (userSelection)
            onChangeSelection({
              ...userSelection,
              parentCompanyId: value,
              companyId,
              dealerId,
            })
        },
        disabled: !isSuperAdmin(),
        placeholder: t(TranslationKey['Amplio partner']),
        style: {height: '2rem', paddingRight: '0.5rem'},
      },
    ],
    [dropdownOptions, userSelection],
  )

  return {
    formFields,
    isDrawerOpen,
    isDownLg,
    setIsDrawerOpen,
  }
}
